import React, { useEffect, useState } from 'react'
import { Stack, TextField, Typography, alpha, useTheme } from '@mui/material'
import { ErrorCode, type AuthFactorType, type IAuthChallengeFactorOption, type IAuthChallengeState, type IAuthSmsOptedOut } from '@repo/types'
import { workosAuthService } from 'services/workos-auth.service'
import { AuthPrimaryButton } from '../AuthPrimaryButton'
import { AuthInlineLink } from '../AuthInlineLink'
import { AuthBackButton } from '../AuthBackButton'
import { authFieldRootSx } from '../authFieldChrome'
import { AUTH_TITLE_SX } from '../authType'
import { CodeInput } from 'components/input'

export interface TwoFactorChallengeVerifiedResult {
  user?: unknown
  isNewUser?: boolean
  usedRecoveryCode?: boolean
  remainingRecoveryCodes?: number
}

export interface TwoFactorChallengeProps {
  ticket: string
  factorType: AuthFactorType
  onVerified: (result: TwoFactorChallengeVerifiedResult) => void
  /** "Back to sign in" — the ticket is abandoned (not burned server-side; it
   * simply expires on its own 5-minute TTL if unused). */
  onBack: () => void
  /** From the login response when the SMS number replied STOP; the options call confirms it. */
  smsOptedOut?: IAuthSmsOptedOut
}

/**
 * VRG-490 §2 batch 6/7 — the SIGN-IN-FLOW second-factor challenge, rendered
 * inline wherever a login call returns `{challengeRequired: true, ticket,
 * factorType}` (SignInPage's OTP/password paths, and AuthCallbackPage for
 * the SSO-redirect path). NOT the enrollment/management surface — that is a
 * separate, deferred batch (see the task file's batch table).
 *
 * Renders the shared `CodeInput` (VRG-610). This file used to hand-roll the six
 * boxes and claimed, in this docblock, to have avoided a second hand-roll by sharing
 * `authOtpBoxSx` — true of the STYLING only. The digit state, auto-advance,
 * backspace-to-previous and paste handling were all duplicated from `SignInPage`,
 * which is how the two came to share a look and differ in behaviour. All of it now
 * lives in `CodeInput`, which is also what gives this screen auto-submit.
 *
 * "Use a recovery code instead" swaps the six boxes for a single free-text
 * field, because a recovery code is NOT six digits — it's generated as
 * `XXXXX-XXXXX` (`generateRecoveryCode`, apps/api `libs/helpers/crypto.ts`).
 * Both submit to the same `POST /auth/2fa/challenge/verify` — the API
 * distinguishes a factor code from a recovery code by looking the value up
 * against both stores, not by a separate field.
 */
/** "***4567" → "4567"; tolerant of a missing value. */
const lastFour = (masked?: string) => (masked ? masked.replace(/\D/g, '').slice(-4) : '')

const switchLabel = (option: IAuthChallengeFactorOption) =>
  option.type === 'totp'
    ? 'Use your authenticator app instead'
    : lastFour(option.maskedPhone)
      ? `Text a code to the phone ending in ${lastFour(option.maskedPhone)} instead${option.optedOut ? ' (texts blocked)' : ''}`
      : `Text a code to your phone instead${option.optedOut ? ' (texts blocked)' : ''}`

export const TwoFactorChallenge = ({ ticket, factorType: initialFactorType, smsOptedOut: initialSmsOptedOut, onVerified, onBack }: TwoFactorChallengeProps) => {
  const theme = useTheme()
  // The user chooses their second factor (Spencer, 2026-10-09). The challenge opens on their
  // preferred factor; `options` are the other enrolled, policy-allowed ones this ticket may
  // switch to (fixed server-side when the ticket was issued).
  const [factorType, setFactorType] = useState<AuthFactorType>(initialFactorType)
  const [currentFactorId, setCurrentFactorId] = useState<string | null>(null)
  const [maskedPhone, setMaskedPhone] = useState<string | undefined>(undefined)
  const [options, setOptions] = useState<IAuthChallengeFactorOption[]>([])
  const [resendIn, setResendIn] = useState(0)
  const [isSwitching, setIsSwitching] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  // The current SMS factor's number replied STOP, so no code was texted (Twilio 21610). The
  // screen then explains it and offers another method, a recovery code, or "Try again" once
  // the person has texted START. Set from the challenge state; cleared by a send that works.
  const [smsOptedOut, setSmsOptedOut] = useState<IAuthSmsOptedOut | null>(initialSmsOptedOut ?? null)

  const applyState = (state: IAuthChallengeState) => {
    setFactorType(state.factorType)
    setCurrentFactorId(state.factorId)
    setMaskedPhone(state.maskedPhone)
    setOptions(state.options)
    setResendIn(state.resendAvailableInSeconds ?? 0)
    setSmsOptedOut(state.smsOptedOut ?? null)
  }

  useEffect(() => {
    let cancelled = false
    workosAuthService.getChallengeOptions(ticket).then((state) => {
      if (!cancelled && state) applyState(state)
    })
    return () => {
      cancelled = true
    }
  }, [ticket])

  // Resend countdown — the API enforces the cooldown; this only stops a dead click.
  useEffect(() => {
    if (resendIn <= 0) return
    const timer = setTimeout(() => setResendIn((value) => Math.max(0, value - 1)), 1000)
    return () => clearTimeout(timer)
  }, [resendIn])

  const alternatives = options.filter((option) => option.factorId !== currentFactorId)

  const handleSwitch = async (option: IAuthChallengeFactorOption) => {
    if (isSwitching) return
    setIsSwitching(true)
    setError(null)
    setNotice(null)
    const result = await workosAuthService.switchChallengeFactor(ticket, option.factorId)
    setIsSwitching(false)
    if (result.state) {
      applyState(result.state)
      setUseRecoveryCode(false)
      setDigits(['', '', '', '', '', ''])
      return
    }
    // SMS_RECIPIENT_OPTED_OUT: `error` is the "text START to …" sentence. The ticket stayed on
    // the current method; refresh the options so the blocked one is labelled.
    setError(result.error ?? 'Could not switch method. Please try again.')
    if (result.retryAfterSeconds) setResendIn(result.retryAfterSeconds)
    if (result.errorCode === ErrorCode.SMS_RECIPIENT_OPTED_OUT) {
      workosAuthService.getChallengeOptions(ticket).then((state) => state && applyState(state))
    }
  }

  const handleResend = async () => {
    if (isSwitching || resendIn > 0) return
    setIsSwitching(true)
    setError(null)
    const wasBlocked = Boolean(smsOptedOut)
    const result = await workosAuthService.resendChallengeCode(ticket)
    setIsSwitching(false)
    if (result.state) {
      applyState(result.state)
      setNotice(wasBlocked ? 'Texts are back on. We sent you a code.' : 'We sent a new code. Only the newest code works.')
      return
    }
    if (result.errorCode === ErrorCode.SMS_RECIPIENT_OPTED_OUT) {
      // Still blocked: the explanation is already on screen, so say only what changed.
      setNotice(null)
      setError('Texts are still blocked. Text START first, then try again.')
      // The attempt still started the resend cooldown; read it back rather than guess it.
      workosAuthService.getChallengeOptions(ticket).then((state) => state && applyState(state))
      return
    }
    setError(result.error ?? 'Could not resend the code. Please try again.')
    if (result.retryAfterSeconds) setResendIn(result.retryAfterSeconds)
  }
  // No code can arrive while the number is blocked, so the code boxes and Verify step aside
  // until the person switches method, opens the recovery-code field, or texts START.
  const textsBlocked = factorType === 'sms' && Boolean(smsOptedOut)
  const [useRecoveryCode, setUseRecoveryCode] = useState(false)
  const [digits, setDigits] = useState(['', '', '', '', '', ''])
  const [recoveryCode, setRecoveryCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [expired, setExpired] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const errorText = alpha(theme.palette.error.main, 0.9)
  const derivedCode = useRecoveryCode ? recoveryCode.trim() : digits.join('')
  // §54 — Submit stays dead until there's something valid to submit. A
  // recovery code has no fixed length (unlike the 6-digit factor code), so
  // "non-empty" is the validity bar for that branch.
  const isComplete = useRecoveryCode ? recoveryCode.trim().length > 0 : digits.every((d) => d !== '')

  /**
   * `submittedCode` is passed by `CodeInput`'s `onComplete`, and taking it is not
   * optional: `onComplete` fires from within the same event that sets the digits, so
   * the `code` derived from state is still one render behind and would submit five
   * digits. The button and Enter paths pass nothing and read state as before.
   */
  const handleSubmit = async (submittedCode?: string) => {
    const code = submittedCode ?? derivedCode
    if (!code || isSubmitting) return
    setIsSubmitting(true)
    setError(null)
    setExpired(false)
    try {
      const result = await workosAuthService.verifyChallenge(ticket, code)
      if (result.success) {
        onVerified({
          user: result.user,
          isNewUser: result.isNewUser,
          usedRecoveryCode: result.usedRecoveryCode,
          remainingRecoveryCodes: result.remainingRecoveryCodes,
        })
        return
      }
      setError(result.error || 'Invalid or expired code')
      setExpired(!!result.expired)
      if (!useRecoveryCode) setDigits(['', '', '', '', '', ''])
      setIsSubmitting(false)
    } catch {
      setError('Invalid or expired code')
      setIsSubmitting(false)
    }
  }

  return (
    <Stack spacing={3}>
      <Typography sx={AUTH_TITLE_SX}>
        Verify it's you
      </Typography>

      <Typography sx={{ fontSize: '14px', lineHeight: 1.5, color: 'text.primary' }} data-testid={!useRecoveryCode && textsBlocked ? 'sms-opted-out-message' : undefined}>
        {useRecoveryCode
          ? 'Enter one of your saved recovery codes.'
          : textsBlocked && smsOptedOut
          ? smsOptedOut.message
          : factorType === 'totp'
          ? 'Enter the 6-digit code from your authenticator app.'
          : lastFour(maskedPhone)
          ? `We texted a 6-digit code to the phone ending in ${lastFour(maskedPhone)}. It expires in 10 minutes.`
          : 'We texted a 6-digit verification code to your phone.'}
      </Typography>

      {useRecoveryCode ? (
        <TextField
          fullWidth
          autoFocus
          placeholder="XXXXX-XXXXX"
          value={recoveryCode}
          onChange={(e) => {
            setRecoveryCode(e.target.value)
            if (error) setError(null)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && recoveryCode.trim()) handleSubmit()
          }}
          error={!!error}
          sx={{
            '& .MuiInputBase-root': authFieldRootSx(theme),
            '& .MuiInputBase-input::placeholder': { color: 'text.secondary', opacity: 1 },
          }}
        />
      ) : textsBlocked ? null : (
        <Stack spacing={2}>
          {/* VRG-610 — the shared `CodeInput`, which is where the digit/advance/
              backspace/paste behaviour now lives (it was duplicated here and in
              `SignInPage`). `onComplete` is the new bit: the code submits the moment
              the sixth digit lands, by typing or by paste.

              It fires on the TRANSITION to complete, not while complete, so a
              rejected code sitting on screen does not resubmit itself as the user
              corrects one digit — the failure mode that makes naive auto-submit
              worse than a button. */}
          <CodeInput
            autoFocus
            value={digits.join('')}
            onChange={(next) => {
              setDigits(Array.from({ length: 6 }, (_, i) => next[i] ?? ''))
              if (error) setError(null)
            }}
            onComplete={(next) => handleSubmit(next)}
            disabled={isSubmitting}
            aria-label="Verification code"
          />
        </Stack>
      )}

      {error && (
        <Typography sx={{ fontSize: '12px', color: errorText, lineHeight: 1.4 }}>
          {error}
        </Typography>
      )}
      {!error && notice && (
        <Typography sx={{ fontSize: '12px', color: 'text.secondary', lineHeight: 1.4 }}>
          {notice}
        </Typography>
      )}

      {/* Same override deleted here as on SignInPage's Submit, for the same reason: it
          re-stated `AuthPrimaryButton`'s own background and opacity, and overrode its ink
          with `alpha(common.white, 0.52)` — white on a near-white fill once the auth
          gradient became `alpha(text.primary, 0.95)`. The component dims
          `background.default` instead, which inverts with the mode. */}
      {!(textsBlocked && !useRecoveryCode) && (
      <AuthPrimaryButton
        fullWidth
        // Wrapped, NOT passed by reference: `handleSubmit` now takes an optional
        // code, so `onClick={handleSubmit}` would hand it the MouseEvent as that
        // argument and submit the event object instead of the digits.
        onClick={() => handleSubmit()}
        disabled={!isComplete || expired}
        loading={isSubmitting}
      >
        Verify
      </AuthPrimaryButton>
      )}

      <Stack spacing={1.5} alignItems="center">
        {!useRecoveryCode && factorType === 'sms' && (
          <Typography sx={{ fontSize: '14px', lineHeight: 1.4, color: 'text.secondary' }}>
            {resendIn > 0 ? (
              textsBlocked ? `You can try again in ${resendIn}s` : `You can request a new code in ${resendIn}s`
            ) : (
              // "Try again" after texting START is the same resend: the API clears the block
              // when the text goes through, and the cooldown / send limits still apply.
              <AuthInlineLink standalone onClick={handleResend}>{textsBlocked ? 'Try again' : 'Resend code'}</AuthInlineLink>
            )}
          </Typography>
        )}
        {!useRecoveryCode &&
          alternatives.map((option) => (
            <Typography key={option.factorId} sx={{ fontSize: '14px', lineHeight: 1.4, color: 'text.secondary' }}>
              <AuthInlineLink standalone onClick={() => handleSwitch(option)}>{switchLabel(option)}</AuthInlineLink>
            </Typography>
          ))}
        <Typography sx={{ fontSize: '14px', lineHeight: 1.4, color: 'text.secondary' }}>
          <AuthInlineLink
            standalone
            onClick={() => {
              setUseRecoveryCode((v) => !v)
              setError(null)
              setExpired(false)
            }}
          >
            {useRecoveryCode ? 'Use your authenticator or SMS code instead' : 'Use a recovery code instead'}
          </AuthInlineLink>
        </Typography>

        {/* The shared auth Back treatment (2026-09-23) — same colour, plate and
            weight as the corner Back and the theme toggle — instead of a
            hand-rolled 14px text.secondary button. */}
        <AuthBackButton placement="inline" label="Back to sign in" onClick={onBack} />
      </Stack>
    </Stack>
  )
}

export default TwoFactorChallenge
